import { createContext, useCallback, useContext, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Button } from '../Button/Button';
import { Dialog } from '../Dialog/Dialog';

export interface ConfirmRequest {
  title: ReactNode;
  text?: ReactNode;
  confirmLabel: string;
  /** Опасное действие (удаление): кнопка подтверждения красная. */
  danger?: boolean;
}

export interface ConfirmDialogProps extends ConfirmRequest {
  open: boolean;
  cancelLabel: string;
  closeLabel: string;
  onConfirm(): void;
  onCancel(): void;
}

/** Подтверждение действия в стиле темы — вместо window.confirm (ADR-011). */
export function ConfirmDialog({ open, title, text, confirmLabel, danger, cancelLabel, closeLabel, onConfirm, onCancel }: ConfirmDialogProps): ReactElement | null {
  return (
    <Dialog
      open={open}
      title={title}
      closeLabel={closeLabel}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{cancelLabel}</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} autoFocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {text ? <p>{text}</p> : null}
    </Dialog>
  );
}

type Confirm = (request: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Один диалог подтверждения на приложение. useConfirm() возвращает функцию-обещание:
 * `if (!(await confirm({…}))) return;`. Подписи кнопок отмены и закрытия передаёт приложение —
 * ui-kit не знает о словарях.
 */
export function ConfirmProvider({ cancelLabel, closeLabel, children }: { cancelLabel: string; closeLabel: string; children: ReactNode }): ReactElement {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((next) => {
    resolver.current?.(false);
    setRequest(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (value: boolean): void => {
    resolver.current?.(value);
    resolver.current = null;
    setRequest(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request ? (
        <ConfirmDialog open {...request} cancelLabel={cancelLabel} closeLabel={closeLabel} onConfirm={() => settle(true)} onCancel={() => settle(false)} />
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('ConfirmProvider не подключён');
  return confirm;
}
